import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Button } from "@mui/material";
import type { ButtonProps } from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CancelButton } from "./CancelButton";

export type ConfirmDialogProps = {
  open: boolean;
  handleClose: () => void;
  onConfirmClick: () => void | Promise<void>;
  title: string;
  description: string;
  cancelButtonText?: string;
  confirmButtonText?: string;
  confirmButtonIcon?: ReactNode;
  confirmButtonColor?: ButtonProps["color"];
};
export function ConfirmDialog({
  open,
  handleClose,
  onConfirmClick,
  title,
  description,
  cancelButtonText,
  confirmButtonText,
  confirmButtonIcon = <CheckCircleIcon />,
  confirmButtonColor = "success",
}: ConfirmDialogProps) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:ConfirmDialog",
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm();
  const defaultConfirmButtonText = t("confirmButtonText", "Confirm");

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSubmitting) {
          handleClose();
        }
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {description}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={handleClose} disabled={isSubmitting}>
          {cancelButtonText}
        </CancelButton>
        <Button
          autoFocus
          color={confirmButtonColor}
          variant="outlined"
          startIcon={confirmButtonIcon}
          onClick={handleSubmit(onConfirmClick)}
          loading={isSubmitting}
        >
          {confirmButtonText ?? defaultConfirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
